import { GridColDef } from '@mui/x-data-grid';
import { Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import AdminDataGrid from 'csam/admin/components/AdminDataGrid';
import AdminPageHeader from 'csam/admin/components/AdminHeader';
import AdminPageLoader from 'csam/admin/components/AdminPageLoader';
import AdminWrapper from 'csam/admin/components/AdminWrapper';
import { getCommonColumns } from 'csam/admin/components/CommonColumns';
import AdminInternalError from 'csam/admin/pages/AdminInternalError';
import { useAuthenticatedMutation, useAuthenticatedQuery } from 'csam/api/api';
import LocaleContext from 'csam/components/LocaleContext';
import { fixed } from 'csam/utils/Constants';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const TrackPhishingTraining = () => {
  const { locale, setLocale } = useContext(LocaleContext);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const deleteMutation = useAuthenticatedMutation<string[], void>(['delete-template'], (ids) => ({
    url: `${fixed}admin/${locale}/email_tracking`,
    method: 'DELETE',
    data: { ids },
  }));

  const queryClient = useQueryClient();

  const handleDelete = (ids?: string[]) => {
    const idsToDelete = ids || selectedIds;
    deleteMutation.mutate(idsToDelete.map((id) => id.toString()) as unknown as void, {
      onSuccess: () => {
        toast.success(`Record Deleted Successfully`);
        queryClient.invalidateQueries({
          queryKey: ['EmailTemplatePage', 'EmailTemplatePageData', locale],
        });
      },
    });
  };

   const columns: GridColDef[] = [
    { field: 'email', headerName: 'Recipient Email', width: 250 },
    { field: 'manager_email', headerName: 'Manager Email', width: 120 },
    { field: 'training_type', headerName: 'Training', width: 120 },
    { field: 'completion_status', headerName: 'Status', width: 120 },
    { field: 'assigned_date', headerName: 'Training assigned date', width: 180, renderCell: (params) =>
  params.value ? new Date(params.value).toLocaleString() : '—' },
    { field: 'last_reminder_sent_date', headerName: 'Last reminder sent', width: 180, renderCell: (params) =>
  params.value ? new Date(params.value).toLocaleString() : '—' },
     // Delete and other actions
  ];

  

  interface PhsihingTrackingData {
  id: number;
  email: string | null;
  manager_email: string;
  training_type: string;
  assigned_date: number;
  completion_status: string | null;
  reminder_count: string | null;
  last_reminder_sent_date: number;
}

  const relations = ['year'];
  const relationsParam = relations.join(',');

  const localeFromStorage = localStorage.getItem('locale') || 'en';
  const { isPending, error, data } = useAuthenticatedQuery<PhsihingTrackingData[]>(
    
    ['EmailTrackingPage', 'EmailTrackingData', locale],
    {
      url: `${fixed}admin/${locale}/appsectrainingdata`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  const formattedDataObject = {
  data:
    data?.success && Array.isArray(data.data)
      ? data.data
          .filter((item) => item.training_type === 'Phishing')
          .map((item) => ({
            ...item,
            id: item.id,
          }))
      : [],
};

const handleDownloadExcel = () => {
  const exportData = formattedDataObject.data.map((item) => ({
    'Recipient Email': item.email,
    'Manager Email': item.manager_email,
    'Training': item.training_type,
    'Completion Status': item.completion_status,
    'Training Assigned Date': item.assigned_date
      ? new Date(item.assigned_date).toLocaleString()
      : '',
    'Last Reminder Sent': item.last_reminder_sent_date
      ? new Date(item.last_reminder_sent_date).toLocaleString()
      : '',
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Training Data');

  XLSX.writeFile(workbook, 'Phishing_Training_Data.xlsx');
};

  if (isPending) return <AdminPageLoader />;
  if (error) return <div>Error: {error.message}</div>;
  if (data && !data.success) return <AdminInternalError />;

  return (
    <AdminWrapper>
      <AdminPageHeader title="Phishing Training Status" locale={locale} setLocale={setLocale}  />
      <Button 
  variant="contained" 
  onClick={handleDownloadExcel}
  style={{ marginBottom: '10px' }}
>
  Download Excel
</Button>
      <AdminDataGrid<PhsihingTrackingData>  data={formattedDataObject} columns={columns} setSelectedIds={setSelectedIds} />
    </AdminWrapper>
  );
};

export default TrackPhishingTraining;
